import React, { useEffect, useState } from "react";
import { useQuery } from "@apollo/client";
import Switch from "@mui/material/Switch";
import "./specialRefinisherPayBlock.css";
import { GET_SERVICE_ITEMS } from "../../utils/GraphQL/queries";
import BdrInput from "../BdrInput/BdrInput";
import {
  priceStrFormatter,
  removePriceStrFormatter,
} from "../../utils/formatters/priceStrFormatter";
import { computeBucketSubtotals } from "../../utils/payBucketsClient";

/**
 * Shared form block for the per-job Special Refinisher Pay override.
 * Used across CreateQuoteModal, ViewClientQuoteModal (Invoice Options),
 * CreateEventModal, EditEventModal, and QuoteInvoiceEditPage.
 *
 * The override decouples the customer-paid invoice amount from the base
 * used to compute refinisher pay (warranty refinish jobs charged at
 * maintenance price). Toggle off → fields hidden, persisted as
 * { enabled: false, amount: null, payType: null }. Toggle on → both
 * amount (cents string, > 0) and payType ("refinish" | "maintenance")
 * are required.
 *
 * Layout when enabled:
 *   [Autofill From Items] [toggle]   [Pay Type ▾]
 *   [Refinisher Pay Base Amount input ────────────]
 *
 * The "Autofill From Items" toggle live-syncs the pay base amount with
 * the sum of the linked invoice/quote line items (excluding tips).
 * Manually editing the amount field flips this toggle off so the user's
 * value isn't clobbered.
 *
 * Theme prop — this block is hosted both on the white quote letterhead and
 * inside the dark admin modals, so it carries both:
 *   - "light" (default) — adds `bdr-field--on-light` to its fields
 *   - "dark" — the default field treatment
 */

/**
 * Compute the autofill base by looking up FRESH prices from the services
 * catalog (matched by itemName, case-insensitive) rather than using the
 * line-item's stored price.
 *
 * Why fresh-from-catalog: the warranty/discount use case has the line-item
 * price intentionally lowered (e.g. a $500 refinish billed at $1.25). Using
 * the on-invoice price would zero-out the refinisher's pay base — exactly
 * what the override is meant to prevent.
 *
 * Custom-named items that don't match any catalog entry fall back to the
 * line-item's stored price so manual additions aren't silently dropped.
 *
 * Tips are always excluded (locked decision: tips remain a separate
 * pay bucket regardless of override).
 *
 * Returns integer cents.
 */
function computeAutofillTotal(items, services, discountPct) {
  if (!Array.isArray(items)) return 0;
  const catalog = services || [];
  const findCatalogPrice = (name) => {
    if (!name) return null;
    const target = name.trim().toLowerCase();
    const hit = catalog.find(
      (s) => s.itemName && s.itemName.trim().toLowerCase() === target
    );
    return hit ? parseInt(hit.price) || 0 : null;
  };

  const gross = items.reduce((sum, item) => {
    if (!item) return sum;
    if (item.itemName && item.itemName.trim().toLowerCase() === "tip") {
      return sum;
    }
    const fresh = findCatalogPrice(item.itemName);
    const unitPrice =
      fresh != null ? fresh : parseInt(item.price) || 0;
    const qty = parseInt(item.quantity) || 1;
    return sum + unitPrice * qty;
  }, 0);

  const pct = parseFloat(discountPct);
  const safePct = isNaN(pct) ? 0 : Math.max(0, Math.min(100, pct));
  return Math.round(gross * (1 - safePct / 100));
}

const SpecialRefinisherPayBlock = ({
  enabled,
  setEnabled,
  amount,
  setAmount,
  payType,
  setPayType,
  inheritedHint,
  theme = "light",
  maxWidth,
  items,
}) => {
  const isDark = theme === "dark";
  const [autofillEnabled, setAutofillEnabled] = useState(false);
  // Default 10% — typical "company cut" applied to the items total when
  // computing what the refinisher should be paid against. Local UI state
  // only; not persisted on the override record.
  const [autofillDiscountPct, setAutofillDiscountPct] = useState("10");

  // Pull the services catalog so autofill uses FRESH catalog prices, not
  // the discounted/warranty prices stored on the invoice line items.
  const { data: servicesData } = useQuery(GET_SERVICE_ITEMS);
  const services = servicesData?.getServicesItems;

  // When autofill is on, keep the amount in sync with catalog-priced items
  // post-discount. Reference equality on `items` is fine — the parent
  // passes a derived array; recomputing on every render with the same
  // value is a no-op because setAmount with an unchanged value won't
  // re-render.
  useEffect(() => {
    if (!autofillEnabled || !enabled) return;
    const next = computeAutofillTotal(items, services, autofillDiscountPct);
    setAmount(String(next));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autofillEnabled, items, enabled, autofillDiscountPct, services]);

  const handleToggle = (event) => {
    const next = event.target.checked;
    setEnabled(next);
    if (!next) {
      setAmount(null);
      setPayType(null);
      setAutofillEnabled(false);
      return;
    }
    if (!payType) setPayType("refinish");
  };

  const handleAutofillToggle = (event) => {
    setAutofillEnabled(event.target.checked);
  };

  const handleAmountChange = (e) => {
    const cents = removePriceStrFormatter(e.target.value);
    if (autofillEnabled) setAutofillEnabled(false);
    setAmount(cents);
  };

  // One field component for both themes — the light surface is a modifier
  // class now rather than a second styled component.
  //
  // This also retires a z-index workaround. The Pay Type picker was a MUI
  // Select whose dropdown is a Popper at z-index 1300, so it rendered BEHIND
  // the modals that host this block and needed `MenuProps={{ sx: { zIndex:
  // 10000 } }}`. A native select's option list is drawn by the OS, above the
  // page entirely, so there is no stacking problem to work around.
  const payFieldClass = isDark ? "" : "bdr-field--on-light";
  const switchSx = {
    "& .MuiSwitch-switchBase.Mui-checked": { color: "#00e676" },
    "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
      backgroundColor: "#00e676",
    },
  };

  // Floating-label-style label for the Switch column. Mirrors MUI's
  // shrunk InputLabel sizing so it visually aligns with the Pay Type
  // and Discount % labels in the same row.
  const floatingLabelStyle = {
    fontSize: "0.75rem",
    fontWeight: 400,
    letterSpacing: "0.00938em",
    color: isDark ? "rgba(255,255,255,0.7)" : "rgba(0,0,0,0.6)",
    marginBottom: "4px",
    lineHeight: 1.4375,
  };

  return (
    <div
      style={{
        marginTop: "10px",
        paddingTop: "6px",
        maxWidth: maxWidth || undefined,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: "8px",
        }}
      >
        <span style={{ fontWeight: "bold", color: isDark ? "#fff" : "#000" }}>
          Special Refinisher Pay
          {inheritedHint && enabled && (
            <span
              style={{
                fontWeight: "normal",
                opacity: 0.7,
                marginLeft: "6px",
                fontSize: "0.85em",
              }}
            >
              ({inheritedHint})
            </span>
          )}
        </span>
        <Switch
          checked={!!enabled}
          onChange={handleToggle}
          size="small"
          sx={switchSx}
        />
      </div>
      {enabled && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: "10px",
            marginTop: "10px",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "flex-end",
              gap: "16px",
              flexWrap: "wrap",
            }}
          >
            {/* Column: Autofill From Items (label on top, toggle below) */}
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                flexShrink: 0,
              }}
            >
              <span style={floatingLabelStyle}>Autofill From Items</span>
              <Switch
                checked={autofillEnabled}
                onChange={handleAutofillToggle}
                size="small"
                sx={switchSx}
              />
            </div>
            {/* Column: Pay Type */}
            <BdrInput
              className={`srp-f-paytype ${payFieldClass}`.trim()}
              label="Pay Type"
              name="srpPayType"
              options={[
                { value: "refinish", label: "Refinish" },
                { value: "maintenance", label: "Maintenance" },
              ]}
              value={payType || "refinish"}
              onChange={(e) => setPayType(e.target.value)}
            />
            {/* Column: Discount % (only when autofill is on) */}
            {autofillEnabled && (
              <BdrInput
                className={`srp-f-pct ${payFieldClass}`.trim()}
                label="Discount %"
                type="number"
                value={autofillDiscountPct}
                onChange={(e) => setAutofillDiscountPct(e.target.value)}
                min={0}
                max={100}
                step={1}
              />
            )}
          </div>
          <BdrInput
            className={payFieldClass}
            label="Refinisher Pay Base Amount"
            placeholder="$0.00"
            value={amount ? priceStrFormatter(amount) : ""}
            onChange={handleAmountChange}
          />

          {/* SRP override preview matrix (2026-05-15b — user request).
              Shows the office user what each pay bucket contributes when
              SRP is enabled so they can see at a glance which portions
              are overridden vs preserved. Pulled from the live items
              list via the client-side bucket mirror in
              utils/payBucketsClient.js (server's payCalc.js is the
              authoritative math — this is a display preview only). */}
          <SrpPreviewMatrix
            items={items}
            amount={amount}
            payType={payType}
            isDark={isDark}
          />
        </div>
      )}
    </div>
  );
};

/**
 * SRP override preview matrix. Shown directly under the Refinisher Pay
 * Base Amount input when SRP is enabled. Sums each preserved 100%-pay
 * bucket from the live items list so the office user can see which
 * portions of the invoice are overridden (refinish/maint) vs preserved
 * on top of the override (tip / hc / tripCharge / other).
 *
 * Why we can't show a single "refinisher gets $X" number: the per-
 * refinisher payPercentage isn't passed into this block, and we don't
 * know how many users are assigned at this layer. So the preview shows
 * the GROSS bucket totals — office can mentally apply percentage +
 * numAssigned, or compare with the payroll report later.
 */
function SrpPreviewMatrix({ items, amount, payType, isDark }) {
  const subtotals = React.useMemo(
    () => computeBucketSubtotals(items),
    [items]
  );
  const overrideCents = parseInt(amount) || 0;

  // Bucket presence flags — only show rows that have a non-zero
  // contribution so the matrix doesn't add noise for invoices that
  // don't have HC/Trip/Other items.
  const rows = [];
  rows.push({
    label: payType === "maintenance" ? "Override (Maintenance base)" : "Override (Refinish base)",
    note: payType === "maintenance" ? "× 50%" : "× refinisher's pay %",
    cents: overrideCents,
    color: isDark ? "#05F842" : "#0a8c2a",
    isOverride: true,
  });
  if (subtotals.tipTotal > 0) {
    rows.push({
      label: "Tip",
      note: "Preserved — split flat among refinishers",
      cents: subtotals.tipTotal,
      color: "#fb8c00",
    });
  }
  if (subtotals.hcTotal > 0) {
    rows.push({
      label: "Hardware Change",
      note: "Preserved — 100%, split flat",
      cents: subtotals.hcTotal,
      color: "#ab47bc",
    });
  }
  if (subtotals.tripChargeTotal > 0) {
    rows.push({
      label: "Trip Charge",
      note: "Preserved — 100%, split flat",
      cents: subtotals.tripChargeTotal,
      color: "#039be5",
    });
  }
  if (subtotals.otherTotal > 0) {
    rows.push({
      label: "Other (Custom pay / legacy parts)",
      note: "Preserved — material-cost already deducted",
      cents: subtotals.otherTotal,
      color: "#f6bf26",
    });
  }
  // subtotals.halfOtherTotal (promo 50/50) and subtotals.halfRefinishTotal
  // deliberately get NO row: like halfTotal and refinishTotal they are
  // REPLACED by the override. 2026-10-06 (owner): the promo row used to be
  // listed as preserved — a Job Type label deciding whether 50% survives an
  // override — and the server no longer does that. SRP replaces the refinish
  // base and every Half line regardless of label; it preserves only the flat
  // buckets above (tip / HC / trip / other+custom).

  const panelBg = isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.04)";
  const panelBorder = isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.12)";
  const labelColor = isDark ? "#fff" : "#000";
  const noteColor = isDark ? "rgba(255,255,255,0.55)" : "rgba(0,0,0,0.55)";

  return (
    <div
      style={{
        marginTop: "4px",
        padding: "10px 12px",
        background: panelBg,
        border: `1px solid ${panelBorder}`,
        borderRadius: "6px",
        fontSize: "12px",
      }}
    >
      <div
        style={{
          fontSize: "11px",
          letterSpacing: "0.5px",
          textTransform: "uppercase",
          color: noteColor,
          marginBottom: "6px",
        }}
      >
        Refinisher pay preview
      </div>
      {rows.map((row, i) => (
        <div
          key={i}
          style={{
            display: "grid",
            gridTemplateColumns: "auto 1fr auto",
            columnGap: "10px",
            alignItems: "baseline",
            padding: "3px 0",
            borderTop: i === 0 ? "none" : `1px solid ${panelBorder}`,
          }}
        >
          <span style={{ color: row.color, fontWeight: 600 }}>{row.label}</span>
          <span style={{ color: noteColor, fontSize: "11px" }}>{row.note}</span>
          <span
            style={{
              color: labelColor,
              fontFamily: "monospace",
              minWidth: "70px",
              textAlign: "right",
            }}
          >
            {priceStrFormatter(String(row.cents))}
            {row.isOverride && payType !== "maintenance" ? " × %" : ""}
            {row.isOverride && payType === "maintenance" ? " × 50%" : ""}
          </span>
        </div>
      ))}
      {rows.length === 1 && (
        <div
          style={{
            marginTop: "6px",
            fontSize: "11px",
            color: noteColor,
            fontStyle: "italic",
          }}
        >
          No preserved-pay items on this invoice — refinisher's take-home
          is just the override × pay %.
        </div>
      )}
    </div>
  );
}

/**
 * Build the GraphQL input payload from the block's state.
 * Always returns a defined object — caller decides whether to omit the
 * arg entirely (pass undefined as the variable) or send it.
 */
export function buildSpecialRefinisherPayInput(enabled, amount, payType) {
  if (!enabled) {
    return { enabled: false, amount: null, payType: null };
  }
  return {
    enabled: true,
    amount: amount ? String(amount) : null,
    payType: payType === "maintenance" ? "maintenance" : "refinish",
  };
}

/**
 * Client-side validation. Returns null when valid, or an error message
 * when enabled but amount is missing/<=0.
 */
export function validateSpecialRefinisherPay(enabled, amount) {
  if (!enabled) return null;
  const cents = parseInt(amount);
  if (isNaN(cents) || cents <= 0) {
    return "Special Refinisher Pay is enabled but the amount is missing or zero.";
  }
  return null;
}

export default SpecialRefinisherPayBlock;
